/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { AsgardeoSPAClient } from '@asgardeo/auth-react';

import { ComponentDisplayType, ComponentTemplateType } from 'types';
import { SourceTypes } from 'types/user-repos';
import { EnvironmentTypes } from 'types/environment';
import { trackingExcludedOrgs } from '../config';
import { publishToAppInsights } from './app-insights';
import logger from './logger';
import {
  publishToMoesif,
  identifyMoesifUser,
  identifyMoesifCompany,
  resetMoesif,
  MoesifUser,
  MoesifOrg,
} from './moesif';

/**
 * A mapping of internal event names to their corresponding Moesif analytics event names.
 *
 * @remarks
 * This map is used to translate application-specific event identifiers into standardized
 * Moesif tracking event names. The keys represent internal event names used throughout
 * the application, while the values represent the corresponding Moesif event names
 * used for analytics tracking.
 *
 * The events cover the following categories:
 * - **Authentication**: Login and signup related events
 * - **Navigation**: Navbar and portal navigation events
 * - **Projects**: Project creation and management events
 * - **Components**: Component lifecycle events including creation, deployment, testing and publishing
 * - **Gateway**: Hybrid gateway creation, update, deletion and deployment events
 * - **DevPortal**: Developer portal related events
 * - **Moesif Dashboard**: Dashboard viewing events
 *
 * @example
 * ```typescript
 * const moesifEventName = MOESIF_EVENT_MAP['login-clickbutton-success'];
 * // moesifEventName === 'Landing-SignIn-Succeeded'
 * ```
 */
type MoesifEventEntry = {
  name: string;
  properties?: Record<string, any>;
};

const MOESIF_EVENT_MAP: Record<string, MoesifEventEntry> = {
  'visit-login-page': { name: 'Landing-SignIn-Viewed' },
  'signup-success': { name: 'Landing-SignUp-Succeeded' },
  'invitation-signup-success': {
    name: 'Landing-SignUp-Succeeded',
    properties: { referral: 'invitation' },
  },
  'login-clickbutton-success': { name: 'Landing-SignIn-Succeeded' },
  'login-clickbutton-error': { name: 'Landing-SignIn-Failed' },
  'visit-home': { name: 'Portal-Viewed-Home' },
  'navbar-home': { name: 'Navbar-Clicked-Home' },
  'navbar-documentation': { name: 'Navbar-Clicked-Documentation' },
  'navbar-user-billing': { name: 'Navbar-Clicked-Billing' },
  'navbar-user-logout': { name: 'Navbar-Clicked-Logout' },
  'navbar-dev-portal': { name: 'Navbar-Clicked-DevPortal' },
  'navbar-project-dropdown-existing': { name: 'Project-Clicked' },
  'project-create-start': { name: 'Project-Created-Start' },
  'project-create-end': { name: 'Project-Created-End' },
  'component-create-start': { name: 'Component-Created-Start' },
  'component-create-end': { name: 'Component-Created-End' },
  'component-delete': { name: 'Component-Deleted' },
  'component-overview': {
    name: 'Component-Viewed-Page',
    properties: { page: 'overview' },
  },
  'component-develop': {
    name: 'Component-Viewed-Page',
    properties: { page: 'develop' },
  },
  'component-test': {
    name: 'Component-Viewed-Page',
    properties: { page: 'test' },
  },
  'component-build': { name: 'Component-Built' },
  'component-deploy': { name: 'Component-Deployed' },
  'component-promote': { name: 'Component-Promoted' },
  'component-test-execute': { name: 'Component-Tested' },
  'component-test-openapi-get-test-key': {
    name: 'Component-Generated-Key',
    properties: { keyType: 'test' },
  },
  'component-manage-lifecycle-state-change-to-publish': {
    name: 'Component-Published',
  },
  'component-manage-lifecycle-state-change-to-demote-to-created': {
    name: 'Component-Unpublished',
  },
  'component-manage-dev-portal': { name: 'DevPortal-Visited' },
  'visit-moesif-dashboard': { name: 'Moesif-Viewed-Dashboard' },
  'hybrid-gateway-create': {
    name: 'Gateway-Created',
    properties: { gateway: 'hybrid' },
  },
  'hybrid-gateway-list-response': {
    name: 'Gateway-Activated',
    properties: { gateway: 'hybrid' },
  },
  'hybrid-gateway-update': {
    name: 'Gateway-Updated',
    properties: { gateway: 'hybrid' },
  },
  'hybrid-gateway-delete': {
    name: 'Gateway-Deleted',
    properties: { gateway: 'hybrid' },
  },
  'hybrid-gateway-deployment-create': {
    name: 'Component-Created-End',
    properties: { gateway: 'hybrid' },
  },
  'hybrid-gateway-deployment-redeploy': {
    name: 'Component-Deployed',
    properties: { gateway: 'hybrid' },
  },
  'hybrid-gateway-deployment-undeploy': {
    name: 'Component-Undeployed',
    properties: { gateway: 'hybrid' },
  },
  'hybrid-gateway-deployment-delete': {
    name: 'Component-Deleted',
    properties: { gateway: 'hybrid' },
  },

  // Component creation - git provider import shortcuts (components/new).
  // One event per provider so the four shortcuts can be told apart in Moesif.
  'component-create-import-github': { name: 'Component-Import-GitHub' },
  'component-create-import-gitlab': { name: 'Component-Import-GitLab' },
  'component-create-import-bitbucket': { name: 'Component-Import-Bitbucket' },
  'component-create-import-azure-devops': {
    name: 'Component-Import-AzureDevOps',
  },

  // Component creation - integration type cards (attach-repository).
  // One event per type, plus a shared fallback so a type added to
  // predefinedComponentTemplate later still reaches Moesif instead of
  // being dropped silently.
  'component-create-select-type-automation': {
    name: 'Component-SelectedType-Automation',
  },
  'component-create-select-type-ai-agent': {
    name: 'Component-SelectedType-AIAgent',
  },
  'component-create-select-type-integration-as-api': {
    name: 'Component-SelectedType-IntegrationAsAPI',
  },
  'component-create-select-type-mcp-server': {
    name: 'Component-SelectedType-MCPServer',
  },
  'component-create-select-type-event-integration': {
    name: 'Component-SelectedType-EventIntegration',
  },
  'component-create-select-type-file-integration': {
    name: 'Component-SelectedType-FileIntegration',
  },
  'component-create-select-type-webhook': {
    name: 'Component-SelectedType-Webhook',
  },
  'component-create-select-type': { name: 'Component-SelectedType' },

  // Create button on the import form. Distinct from 'component-create-end',
  // which keeps its current meaning for the existing dashboards.
  'component-create-submit': { name: 'Component-Created-Submit' },
  'component-create-multiple-start': { name: 'Component-CreateMultiple-Start' },

  // Existing create-flow events that were reaching App Insights only.
  'open-cloud-editor-click': { name: 'CloudEditor-Opened' },
  'quick-deploy-click': { name: 'Sample-Deployed' },
  'components-new-browse-more-samples': { name: 'Sample-Explored' },
  'featured-prebuilt-integration-click': {
    name: 'PrebuiltIntegration-Clicked',
  },
  'prebuilt-integration-explore-click': {
    name: 'PrebuiltIntegration-Explored',
  },
  'components-new-get-started-tab': {
    name: 'GetStartedQuickly-TabClicked',
  },
};

const convertKeysToSnakeCase = (
  obj: Record<string, any>
): Record<string, any> =>
  Object.entries(obj).reduce((acc, [key, value]) => {
    const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
    acc[snakeKey] = value;
    return acc;
  }, {} as Record<string, any>);

const getMoesifEventName = (name: string): MoesifEventEntry | undefined =>
  MOESIF_EVENT_MAP[name];

export const trackEvent = async (
  name: string,
  customProperties?: any,
  doIdentify?: boolean
) => {
  const orgUuid =
    sessionStorage.getItem('orgUuid') || customProperties?.org || '';

  if (orgUuid && trackingExcludedOrgs.includes(orgUuid)) return;

  const orgHandle = sessionStorage.getItem('orgHandle') || '';
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;
  let userEmail;
  let userDisplayName;

  const properties: Record<string, any> = {
    ...customProperties,
    ...(orgUuid ? { org: orgUuid } : {}),
    context: 'console',
    origin: 'devant',
    windowWidth: `${windowWidth}`,
    windowHeight: `${windowHeight}`,
  };

  // Add user info
  const auth = AsgardeoSPAClient.getInstance();
  if (auth) {
    try {
      const basicUserInfo = await auth.getBasicUserInfo();
      if (basicUserInfo?.sub) {
        properties.idpId = basicUserInfo.sub;
      }
      if (basicUserInfo?.email) {
        userEmail = basicUserInfo.email;
        properties.isWSO2User = String(
          basicUserInfo.email.endsWith('@wso2.com')
        );
      }
      userDisplayName = basicUserInfo?.displayName;
    } catch (error) {
      logger.warn('Failed to get auth details for moesif event', error);
    }
  }

  // Publish to Azure (only if consent is given)
  publishToAppInsights(name, properties);

  // Identify user and company in Moesif if explicitely mentioned
  if (doIdentify) {
    const moesifOrg: MoesifOrg | undefined = orgUuid
      ? {
          uuid: orgUuid,
          handle: orgHandle || undefined,
          ...(customProperties?.moesifOrgId
            ? {
                metadata: {
                  moesif_analytics_org_id: customProperties.moesifOrgId,
                },
              }
            : {}),
        }
      : undefined;

    const moesifUser: MoesifUser | undefined = {
      id: properties.idpId,
      email: userEmail,
      name: userDisplayName,
      isWSO2User: properties.isWSO2User,
    };

    if (moesifUser) identifyMoesifUser(moesifUser);
    if (moesifOrg) identifyMoesifCompany(moesifOrg);
  }

  // Publish to Moesif (Legacy event)
  // publishToMoesif(name, properties);

  const moesifGenericProperties: Record<string, any> = {
    product: 'integration',
    asset_type: 'console',
    domain: window.location.hostname,
    deployment_model: 'saas', // This has to be either 'saas', 'self-hosted', or 'private-cloud'
    // context: '' // This is not required for console
    idp_id: properties.idpId,
    is_wso2_user: properties.isWSO2User,
    ...convertKeysToSnakeCase(customProperties || {}),
  };

  // Publish to Moesif (New event)
  const moesifEvent = getMoesifEventName(name);
  if (moesifEvent) {
    publishToMoesif(moesifEvent.name, {
      ...moesifGenericProperties,
      ...moesifEvent.properties,
    });
  }
};

const convertNameToKebabCase = (str?: string) => {
  const matchStr =
    str &&
    str.match(
      /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g
    );

  return matchStr && matchStr.map((x) => x.toLowerCase()).join('-');
};

export const trackLoginPageVisit = () => {
  trackEvent('visit-login-page');
};

/**
 * A Copilot prompt submitted from the login page, before the visitor has an
 * account. The prompt itself is deliberately not sent — only its length, as a
 * rough signal of how much detail visitors give up front.
 */
export const trackLoginCopilotPromptSubmit = (promptLength: number) => {
  trackEvent('login-copilot-prompt-submit', {
    promptLength,
  });
};

export const trackLoginExploreIntegrationsClick = () => {
  trackEvent('login-explore-integrations-click');
};

/**
 * A pair of applications picked from the prebuilt integrations row on the login
 * page, before the visitor has an account. The names are the published
 * catalogue's, not anything the visitor wrote.
 */
export const trackLoginPrebuiltIntegrationClick = (applications: string[]) => {
  trackEvent('login-prebuilt-integration-click', {
    applications: applications.join(','),
  });
};

export const trackChoreoLogoClick = () => {
  trackEvent('navbar-home');
};

export const trackRecentComponentItemClick = () => {
  trackEvent('home-recents-component');
};

export const trackPerspectiveSelection = (selectedPerspective: string) => {
  trackEvent('perspective-selection', {
    selectedPerspective,
  });
};

export const trackComponentCreateStart = (
  orgUuid: string,
  location:
    | 'nav-bar'
    | 'project-grid'
    | 'component-list'
    | 'mono-repo'
    | 'empty-project-home',
  projectId?: string
) => {
  trackEvent('component-create-start', {
    org: orgUuid,
    project: projectId,
    location,
  });
};

export const trackComponentCreateIntermediate = (
  orgUuid: string,
  step:
    | 'select-type'
    | 'select-buildpack'
    | 'select-mono-repo-component'
    | 'save-mono-repo-config'
    | 'select-git-provider',

  stepInput?: string
) => {
  trackEvent('component-create-intermediate', {
    org: orgUuid,
    step,
    stepInput,
  });
};

/**
 * Git provider import shortcuts on the "Create a new integration" page.
 *
 * Each provider has its own event name so the four shortcuts are separate
 * series in Moesif rather than one event distinguished by a property.
 */
const GIT_PROVIDER_IMPORT_EVENTS: Partial<Record<SourceTypes, string>> = {
  [SourceTypes.GITHUB]: 'component-create-import-github',
  [SourceTypes.GITLAB]: 'component-create-import-gitlab',
  [SourceTypes.BIT_BUCKET]: 'component-create-import-bitbucket',
  [SourceTypes.AZURE_DEVOPS]: 'component-create-import-azure-devops',
};

export const trackComponentImportProviderClick = (
  provider: SourceTypes,
  orgUuid: string,
  projectId?: string
) => {
  const eventName = GIT_PROVIDER_IMPORT_EVENTS[provider];
  // Only the four git providers have an import shortcut on the page.
  if (!eventName) return;
  trackEvent(eventName, {
    org: orgUuid,
    project: projectId,
    provider,
  });
};

/**
 * Integration type cards on the import (attach-repository) page.
 *
 * One event name per type. Anything not listed falls back to the generic
 * 'component-create-select-type' event, which is mapped to Moesif too, so a
 * newly added type is never dropped without anyone noticing.
 */
const INTEGRATION_TYPE_EVENTS: Partial<Record<ComponentTemplateType, string>> =
  {
    [ComponentTemplateType.ScheduleTask]:
      'component-create-select-type-automation',
    [ComponentTemplateType.AiAgent]: 'component-create-select-type-ai-agent',
    [ComponentTemplateType.Microservices]:
      'component-create-select-type-integration-as-api',
    [ComponentTemplateType.MCPServer]:
      'component-create-select-type-mcp-server',
    [ComponentTemplateType.IntegrationWebhook]:
      'component-create-select-type-event-integration',
    [ComponentTemplateType.FileIntegration]:
      'component-create-select-type-file-integration',
    [ComponentTemplateType.BallerinaServiceWebhookSubtype]:
      'component-create-select-type-webhook',
  };

export const trackIntegrationTypeSelect = (
  integrationType: ComponentTemplateType,
  orgUuid: string,
  projectId?: string
) => {
  trackEvent(
    INTEGRATION_TYPE_EVENTS[integrationType] || 'component-create-select-type',
    {
      org: orgUuid,
      project: projectId,
      integrationType,
    }
  );
};

/**
 * Fired when Create is pressed on the import form, before the creation
 * request goes out. 'component-create-end' still fires where it always did,
 * so existing dashboards are unaffected; the gap between the two is the
 * submit-to-completion drop off.
 */
export const trackComponentCreateSubmit = (
  orgUuid: string,
  projectId: string,
  integrationType?: string,
  buildpack?: string
) => {
  trackEvent('component-create-submit', {
    org: orgUuid,
    project: projectId,
    integrationType,
    buildpack,
  });
};

export const trackCreateMultipleComponentsStart = (
  orgUuid: string,
  projectId?: string
) => {
  trackEvent('component-create-multiple-start', {
    org: orgUuid,
    project: projectId,
  });
};

export const trackBrowseMoreSamplesClick = (
  orgUuid: string,
  projectId?: string
) => {
  trackEvent('components-new-browse-more-samples', {
    org: orgUuid,
    project: projectId,
  });
};

export const trackGetStartedQuicklyTabClick = (
  tab: string,
  orgUuid: string,
  projectId?: string
) => {
  trackEvent('components-new-get-started-tab', {
    org: orgUuid,
    project: projectId,
    tab,
  });
};

export const trackComponentCreateEnd = (
  projectId: string,
  location: 'mono-repo-import' | 'component-form',
  componentType: string,
  buildpack: string,
  orgUuid: string
) => {
  trackEvent('component-create-end', {
    project: projectId,
    location,
    componentType,
    buildpack,
    org: orgUuid,
  });
};

export const trackProjectCreateStart = (
  orgUuid: string,
  location: 'nav-bar' | 'project-grid',
  isDefaultProject: boolean
) => {
  trackEvent('project-create-start', {
    org: orgUuid,
    location,
    isDefaultProject,
  });
};

export const trackProjectCreateIntermediate = (
  orgUuid: string,
  step: 'enter-basic-details' | 'select-git-provider' | 'select-git-repo',
  stepInput?: string
) => {
  trackEvent('project-create-intermediate', {
    org: orgUuid,
    step,
    stepInput,
  });
};

export const trackProjectCreateEnd = (
  orgUuid: string,
  projectId: string,
  region: string,
  isDefaultProject: boolean
) => {
  trackEvent('project-create-end', {
    project: projectId,
    projectRegion: region,
    org: orgUuid,
    isDefaultProject,
  });
};

export const trackTriggerBuild = (
  componentId: string,
  projectId: string,
  orgUuid: string
) => {
  trackEvent('component-build', {
    component: componentId,
    project: projectId,
    org: orgUuid,
  });
};

export const trackTriggerAutoDeploy = (
  componentId: string,
  projectId: string,
  orgUuid: string,
  autoDeploy: boolean
) => {
  trackEvent('component-build-intermediate', {
    component: componentId,
    project: projectId,
    autoDeploy: autoDeploy ? 'true' : 'false',
    org: orgUuid,
  });
};

export const trackTriggerDeploy = (
  componentId: string,
  projectId: string,
  orgUuid: string
) => {
  trackEvent('component-deploy', {
    component: componentId,
    project: projectId,
    org: orgUuid,
  });
};

export const trackTriggerDeployIntermediate = (
  componentId: string,
  projectId: string,
  orgUuid: string,
  location: string
) => {
  trackEvent('component-deploy-intermediate', {
    component: componentId,
    project: projectId,
    org: orgUuid,
    location,
  });
};

export const trackTriggerPromote = (
  componentId: string,
  projectId: string,
  orgUuid: string,
  envFrom: string,
  envTo: string
) => {
  trackEvent('component-promote', {
    component: componentId,
    project: projectId,
    org: orgUuid,
    envFrom,
    envTo,
  });
};

export const trackVisitHome = (orgUuid: string) => {
  trackEvent('visit-home', {
    org: orgUuid,
  });
};

export const trackJoinDemoOrg = () => {
  trackEvent('join-demo-org');
};

export const trackSelectExistingProject = (
  orgUuid: string,
  projectName?: string
) => {
  trackEvent('navbar-project-dropdown-existing', {
    project: projectName,
    org: orgUuid,
  });
};

export const trackBuildAreaDeploy = (orgUuid: string, componentId?: string) => {
  trackEvent('component-deploy-build-area-deploy', {
    component: componentId,
    org: orgUuid,
  });
};

export const trackConfigureClick = (
  componentId?: string,
  envName?: string,
  orgId?: string
) => {
  if (envName === EnvironmentTypes.ProdEnv) {
    trackEvent('component-deploy-dev-configure', {
      component: componentId,
      org: orgId,
    });
  } else if (envName === EnvironmentTypes.DevEnv) {
    trackEvent('component-deploy-build-configure', {
      component: componentId,
      org: orgId,
    });
  }
};

export const trackConfigurableSaveClick = (
  componentId?: string,
  envName?: string,
  orgId?: string
) => {
  if (envName === EnvironmentTypes.ProdEnv) {
    trackEvent('component-deploy-dev-promote', {
      component: componentId,
      org: orgId,
    });
  } else if (envName === EnvironmentTypes.DevEnv) {
    trackEvent('component-deploy-build-area-deploy', {
      component: componentId,
      org: orgId,
    });
  }
};

export const trackBuildViewLogsClick = (
  componentId?: string,
  orgId?: string
) => {
  trackEvent('component-deploy-build-area-view-logs', {
    component: componentId,
    org: orgId,
  });
};

export const trackComponentOverviewNavClick = (
  componentId?: string,
  orgId?: string
) => {
  trackEvent('component-overview', {
    component: componentId,
    org: orgId,
  });
};

export const trackDevelopNavClick = (orgUuid: string, componentId?: string) => {
  trackEvent('component-develop', {
    component: componentId,
    org: orgUuid,
  });
};

export const trackExecutionsNavClick = (
  componentId?: string,
  orgId?: string
) => {
  trackEvent('component-executions', {
    component: componentId,
    org: orgId,
  });
};

export const trackTestNavClick = (
  location: string,
  componentId?: string,
  projectID?: string,
  orgUuid?: string
) => {
  trackEvent('component-test', {
    location,
    component: componentId,
    project: projectID,
    org: orgUuid,
  });
};

export const trackManageNavClick = (componentId?: string, orgId?: string) => {
  trackEvent('component-manage', {
    component: componentId,
    org: orgId,
  });
};

export const trackSaveApiKey = (componentId?: string, orgId?: string) => {
  trackEvent('component-test-postman-add', {
    component: componentId,
    org: orgId,
  });
};

export const trackTestApiExecuteClick = (
  orgUuid: string,
  componentId?: string
) => {
  trackEvent('component-test-execute', {
    component: componentId,
    org: orgUuid,
  });
};

export const trackOpenApiGetTestKeyClick = (
  componentId?: string,
  orgId?: string
) => {
  trackEvent('component-test-openapi-get-test-key', {
    component: componentId,
    org: orgId,
  });
};

export const trackSidebarHomeClick = () => {
  trackEvent('sidebar-home');
};

export const trackSidebarInsightsClick = () => {
  trackEvent('sidebar-insights');
};

export const trackSidebarComponentsClick = () => {
  trackEvent('sidebar-components');
};

export const trackSidebarDataplanesClick = () => {
  trackEvent('sidebar-dataplanes');
};

export const trackSidebarEnvironmentsClick = () => {
  trackEvent('sidebar-environments');
};

export const trackSidebarHelmMarketplaceClick = () => {
  trackEvent('sidebar-helm-marketplace');
};

export const trackComponentPageLoad = (
  orgUuid: string,
  projectId?: string,
  componentId?: string,
  newComponent?: boolean
) => {
  trackEvent('component-page-load', {
    org: orgUuid,
    project: projectId,
    component: componentId,
    newComponent: `${newComponent}`,
  });
};

export const trackComponentTabClose = (
  orgUuid: string,
  projectId?: string,
  componentId?: string
) => {
  trackEvent('component-tab-close', {
    org: orgUuid,
    project: projectId,
    component: componentId,
  });
};

export const trackOwnRepoViewPrLink = (projectId?: string) => {
  trackEvent('component-view-pr-with-own-repo', {
    project: projectId,
  });
};

export const trackOptionalPathSelectClick = (
  projectId: string,
  userManagedNonEmpty: boolean
) => {
  trackEvent('component-select-show-optional-with-own-repo', {
    project: projectId,
    createWithOwnRepo: userManagedNonEmpty ? 'exiting' : 'empty',
  });
};

export const trackUserSignUpSuccess = (
  queryParams?: [string, string][],
  orgUuid?: string,
  orgHandle?: string
) => {
  const additionalParams: { [key: string]: string } = {
    org: orgUuid || '',
    orgHandle: orgHandle || '',
  };
  // Append query params to addtionalProperties if available
  if (queryParams && queryParams.length > 0) {
    queryParams.forEach(([name, value]) => {
      additionalParams[name] = value;
    });
  }
  trackEvent('signup-success', additionalParams);
};

export const trackUserInvitationSignUpSuccess = () => {
  trackEvent('invitation-signup-success');
};

export const trackLoginSuccess = ({
  isOrgSwitch = false,
  moesifOrgId,
}: {
  isOrgSwitch?: boolean;
  moesifOrgId?: string | null;
} = {}) => {
  resetMoesif();
  trackEvent(
    'login-clickbutton-success',
    {
      isOrgSwitch,
      ...(moesifOrgId ? { moesifOrgId } : {}),
    },
    true
  );
};

export const trackUserLoginError = (returnUrl?: string, isSilent?: boolean) => {
  trackEvent('login-clickbutton-error', {
    url: returnUrl,
    silentLogin: isSilent,
  });
};

export enum OpenWithVSCodeTriggerPath {
  HOME = 'home',
  COMPONENTS = 'components',
}

export const trackOpenProjectWithVSCodeClick = (
  triggerPath: OpenWithVSCodeTriggerPath,
  projectId: string
) => {
  if (triggerPath === OpenWithVSCodeTriggerPath.HOME) {
    trackEvent('home-project-open-with-vscode', {
      project: projectId,
    });
  } else if (triggerPath === OpenWithVSCodeTriggerPath.COMPONENTS) {
    trackEvent('components-project-open-with-vscode', {
      project: projectId,
    });
  }
};

export const trackGetVSCodeClick = (
  triggerPath: OpenWithVSCodeTriggerPath,
  projectId: string
) => {
  if (triggerPath === OpenWithVSCodeTriggerPath.HOME) {
    trackEvent('home-project-get-vscode', {
      project: projectId,
    });
  } else if (triggerPath === OpenWithVSCodeTriggerPath.COMPONENTS) {
    trackEvent('components-project-get-vscode', {
      project: projectId,
    });
  }
};

export const trackGetChoreoExtensionClick = (
  triggerPath: OpenWithVSCodeTriggerPath,
  projectId: string
) => {
  if (triggerPath === OpenWithVSCodeTriggerPath.HOME) {
    trackEvent('home-project-get-choreo-extension', {
      project: projectId,
    });
  } else if (triggerPath === OpenWithVSCodeTriggerPath.COMPONENTS) {
    trackEvent('components-project-get-choreo-extension', {
      project: projectId,
    });
  }
};

export const trackDocumentationNavClick = () => {
  trackEvent('navbar-documentation');
};

export const trackBillingNavClick = () => {
  trackEvent('navbar-user-billing');
};

export const trackLogoutNavClick = () => {
  trackEvent('navbar-user-logout');
};

export const trackSampleGetStartedClick = (projectId?: string) => {
  trackEvent('components-new-create-sample-getstarted', {
    project: projectId,
  });
};

export const trackSampleComponentTypeClick = (componentType?: string) => {
  let sectionName = 'home-existing-project';
  if (window?.location?.pathname?.endsWith('components/new')) {
    sectionName = 'components-new';
  }

  let componentTypeStr = componentType;
  switch (componentType) {
    case ComponentDisplayType.RestApi:
      componentTypeStr = 'httpApi';
      break;
    case ComponentDisplayType.Proxy:
    case ComponentDisplayType.GitProxy:
      componentTypeStr = 'httpProxyApi';
      break;
    default:
      break;
  }
  trackEvent(
    `${sectionName}-click-create-component-${convertNameToKebabCase(
      componentTypeStr
    )}`
  );
};

export const trackSampleViewAllClick = (projectId?: string) => {
  trackEvent('home-existing-project-view-samples', {
    project: projectId,
  });
};

export const trackMarketplaceTabClick = (tabName: string) => {
  trackEvent(`marketplace-${tabName}-click`);
};

export const trackManageOverviewClick = (componentId?: string) => {
  trackEvent('component-manage-overview', {
    component: componentId,
  });
};

export const trackManageLifeCycleClick = (componentId?: string) => {
  trackEvent('component-manage-lifecycle', {
    component: componentId,
  });
};

export const trackManageConsumersClick = (componentId?: string) => {
  trackEvent('component-manage-consumers', {
    component: componentId,
  });
};

export const trackManageDocumentsClick = (componentId?: string) => {
  trackEvent('component-manage-documents', {
    component: componentId,
  });
};

export const trackManageUsagePlansClick = (componentId?: string) => {
  trackEvent('component-manage-usage-plans', {
    component: componentId,
  });
};

export const trackManageSecuritySave = (componentId?: string) => {
  trackEvent('component-manage-settings-security-save', {
    component: componentId,
  });
};

export const trackManageResourcesSave = (componentId?: string) => {
  trackEvent('component-manage-settings-resources-save ', {
    component: componentId,
  });
};

export const trackManageSettingsClick = (componentId?: string) => {
  trackEvent('component-manage-settings', {
    component: componentId,
  });
};

export const trackManagePropertiesClick = (componentId?: string) => {
  trackEvent('component-manage-properties', {
    component: componentId,
  });
};

export const trackManageScopesClick = (componentId?: string) => {
  trackEvent('component-manage-scopes', {
    component: componentId,
  });
};

export const trackApimAnalyticsPortalEvents = (
  eventName: string,
  customProperties = {}
) => {
  trackEvent(eventName, customProperties);
};

export const trackAPIAnalyticsNavigationFromDoc = () => {
  trackEvent('api-analytics-refer-documentation');
};

export const trackOnPremKeyTabNavigation = () => {
  trackEvent('api-analytics-on-prem-keys');
};

export const trackEnvironmentNameUserInput = () => {
  trackEvent('api-analytics-add-env');
};

export const trackOnPremKeyGeneration = () => {
  trackEvent('api-analytics-generate-key');
};

export const trackOnPremKeyRegeneration = () => {
  trackEvent('api-analytics-regenerate-key');
};

export const trackOnPremKeyCopyEvent = () => {
  trackEvent('api-analytics-copy-key');
};

export const trackWSO2ContactLinkClickEvent = () => {
  trackEvent('api-analytics-contact-wso2');
};

export const trackMarketplaceItemClick = (properties: {
  type?: string;
  organization?: string;
  name?: string;
  version?: string;
  searchText?: string | null;
  searchCategory?: string | null;
  searchFilterBy?: string | null;
  searchPublishedBy?: string | null;
  sortValue?: string | null;
}) => {
  const customProperties: any = {
    type: properties.type,
    organization: properties.organization,
    name: properties.name,
    version: properties.version,
  };
  if (properties.searchText) {
    customProperties.querySearch = properties.searchText;
  }
  if (properties.sortValue) {
    customProperties.querySort = properties.sortValue;
  }
  if (properties.searchCategory) {
    customProperties.queryCategory = properties?.searchCategory;
    const [mainCategory, subCategory] = properties.searchCategory.split('/');
    customProperties.mainCategory = mainCategory;
    if (subCategory) {
      customProperties.subCategory = subCategory;
    }
  }
  if (properties.searchPublishedBy) {
    customProperties.queryPublishedBy = properties.searchPublishedBy;
  }
  if (properties.searchFilterBy) {
    customProperties.queryFilterBy = properties.searchFilterBy;
  }

  trackEvent('marketplace-item-click', customProperties);
};

export const trackOnMarketplaceItemLoad = (properties: {
  searchText?: string | null;
  selectedCategory?: string;
  filterByValues?: string[];
  sortValue?: string | null;
  publishedOrg?: string | null;
  type?: string;
  resultsCount?: number;
}) => {
  const {
    searchText,
    selectedCategory,
    filterByValues,
    sortValue,
    publishedOrg,
    type,
    resultsCount,
  } = properties;
  if (
    searchText ||
    selectedCategory ||
    filterByValues?.length ||
    sortValue ||
    publishedOrg
  ) {
    const customProperties: any = {};
    customProperties.type = type;
    customProperties.resultsCount = resultsCount ? `${resultsCount}` : '0';
    if (publishedOrg) {
      customProperties.queryPublishedBy = publishedOrg;
    }
    if (sortValue) {
      customProperties.querySort = sortValue;
    }
    if (searchText) {
      customProperties.querySearch = searchText;
    }

    if (selectedCategory) {
      customProperties.queryCategory = selectedCategory;
      const [mainCategory, subCategory] = selectedCategory.split('/');
      customProperties.mainCategory = mainCategory;
      if (subCategory) {
        customProperties.subCategory = subCategory;
      }
    }
    if (filterByValues?.length) {
      customProperties.queryFilterBy = filterByValues.join(',');
    }

    trackEvent('marketplace-item-list-load', customProperties);
  }
};

export const trackMarketplaceDocumentationClick = (properties: {
  type?: string;
  organization?: string;
  name?: string;
  version?: string;
}) => {
  trackEvent('marketplace-item-view-api-documentation-click', properties);
};

export const trackMarketplaceUseThisClick = (properties: {
  type?: string;
  organization?: string;
  name?: string;
  version?: string;
}) => {
  trackEvent('marketplace-item-view-use-this-click', properties);
};

export const trackApiLifeCycleStateChange = (
  apiNewState?: string,
  projectId?: string,
  componentId?: string
) => {
  const newComponentStr = `component-manage-lifecycle-state-change-to-${convertNameToKebabCase(
    apiNewState
  )}`;
  trackEvent(newComponentStr, {
    project: projectId,
    component: componentId,
  });
};

export const trackPublishAddToMarketplace = (
  visibility?: string,
  projectId?: string,
  componentId?: string
) => {
  trackEvent(`component-manage-publish-to-marketplace`, {
    project: projectId,
    component: componentId,
    visibility,
  });
};

export const trackViewInMarketplace = (
  projectId?: string,
  componentId?: string
) => {
  trackEvent(`component-manage-view-in-marketplace`, {
    project: projectId,
    component: componentId,
  });
};

export const trackDevPortalNavClick = (orgUuid: string) => {
  trackEvent('navbar-dev-portal', {
    org: orgUuid,
  });
};

export const trackLifeCycleDevPortalNavClick = (apiId?: string) => {
  trackEvent('component-manage-dev-portal', { api: apiId });
};

export const trackObserverSampleConfirmClick = (
  componentId?: string,
  projectId?: string
) => {
  trackEvent('component-observe-sample-confirm-click', {
    component: componentId,
    project: projectId,
  });
};

export const trackObserveSubNavClick = (
  sectionName: string,
  componentId?: string,
  projectId?: string,
  isSample?: boolean
) => {
  trackEvent(`component-observe-${sectionName?.toLowerCase()}-tab-click`, {
    component: componentId,
    project: projectId,
    isSample: `${isSample}`,
  });
};

export const trackObserveThroughputClick = (
  componentId?: string,
  projectId?: string,
  isSample?: boolean
) => {
  trackEvent(`component-observe-throughput-graph-click`, {
    component: componentId,
    project: projectId,
    isSample: `${isSample}`,
  });
};

export const trackComponentDeletion = (
  componentId?: string,
  projectId?: string
) => {
  trackEvent('component-delete', {
    component: componentId,
    project: projectId,
  });
};

export const trackMoesifAPIKeySave = (
  appId: string,
  orgUuid: string,
  envType: string
) => {
  trackEvent('moesif-api-key-save', {
    applicationId: appId,
    environmentType: envType,
    org: orgUuid,
  });
};

export const trackVisitMoesifDashboard = (orgUuid: string) => {
  trackEvent('visit-moesif-dashboard', {
    org: orgUuid,
  });
};

export const trackDBCreateStartClick = (orgUuid: string) => {
  trackEvent('db-create-btn-click', {
    org: orgUuid,
  });
};

export const trackDBCreateIntermediate = (orgUuid: string, dbType: string) => {
  trackEvent('db-create-intermediate', {
    org: orgUuid,
    databaseType: dbType,
  });
};

export const trackDBCreateEnd = (
  orgUuid: string,
  cloudProvider: string,
  region: string,
  servicePlan: string
) => {
  trackEvent('db-create-end', {
    org: orgUuid,
    cloudProvider,
    region,
    servicePlan,
  });
};

export const trackVisitDatabaseCreationPage = (orgUuid: string) => {
  trackEvent('visit-db-creation-page', {
    org: orgUuid,
  });
};

export const trackMessageBrokerCreatePage = (orgUuid: string) => {
  trackEvent('visit-message-broker-creation-page', {
    org: orgUuid,
  });
};

export const trackMessageBrokerCreateStartClick = (orgUuid: string) => {
  trackEvent('mb-create-btn-click', {
    org: orgUuid,
  });
};

export const trackQuickDeployClick = (
  orgUuid: string,
  projectId: string,
  sampleName: string
) => {
  trackEvent('quick-deploy-click', {
    org: orgUuid,
    project: projectId,
    sample: sampleName,
  });
};

export const trackQuickDeployBannerTryButtonClick = (
  projectId: string,
  componentHandle: string,
  componentType: ComponentDisplayType
) => {
  trackEvent('quick-deploy-banner-try-button-click', {
    project: projectId,
    componentName: componentHandle,
    componentType,
  });
};

export const trackPrebuiltIntegrationDeployClick = (
  orgUuid: string,
  projectId: string,
  integrationName: string
) => {
  trackEvent('prebuilt-integration-deploy-click', {
    org: orgUuid,
    project: projectId,
    integration: integrationName,
  });
};

export const trackNavigateToPrebuiltIntegrationSetup = (
  orgUuid: string,
  projectId: string,
  integrationName: string
) => {
  trackEvent('navigate-to-prebuilt-integration-setup-click', {
    org: orgUuid,
    project: projectId,
    integration: integrationName,
  });
};

export const trackPrebuiltIntegrationExploreClick = (
  orgUuid: string,
  projectId: string
) => {
  trackEvent('prebuilt-integration-explore-click', {
    org: orgUuid,
    project: projectId,
  });
};

export const trackFeaturedPrebuiltIntegrationClick = (
  orgUuid: string,
  projectId: string,
  integrationName: string
) => {
  trackEvent('featured-prebuilt-integration-click', {
    org: orgUuid,
    project: projectId,
    integration: integrationName,
  });
};

export const trackOpenCloudEditorClick = (
  orgUuid: string,
  projectId: string
) => {
  trackEvent('open-cloud-editor-click', {
    org: orgUuid,
    project: projectId,
  });
};

// Left menu selection events
export const trackLeftMenuSelection = (menuName: string) => {
  trackEvent('left-menu-selection', {
    menuName: convertNameToKebabCase(menuName),
  });
};

// Hybrid Gateway (Self-Hosted Gateway) events
export const trackHybridGatewayCreate = (
  orgUuid: string | undefined,
  gatewayId: string,
  functionalityType: string,
  environmentId?: string,
  isActive?: boolean
) => {
  trackEvent('hybrid-gateway-create', {
    org: orgUuid,
    gatewayId,
    functionalityType,
    environmentId,
    isActive: isActive === undefined ? undefined : String(isActive),
  });
};

/**
 * Track gateway list response to check status of gateways (active vs inactive).
 * Call after fetching the list of gateways (e.g. after create refetch or initial load).
 */
export const trackHybridGatewayListResponse = (
  orgUuid: string | undefined,
  params: {
    totalCount: number;
    activeCount: number;
    inactiveCount: number;
    /** Optional: trigger context e.g. 'after-create' when list was refetched after creating a gateway */
    trigger?: 'initial' | 'refetch' | 'after-create';
  }
) => {
  const { totalCount, activeCount, inactiveCount, trigger } = params;
  trackEvent('hybrid-gateway-list-response', {
    org: orgUuid,
    totalCount: `${totalCount}`,
    activeCount: `${activeCount}`,
    inactiveCount: `${inactiveCount}`,
    ...(trigger ? { trigger } : {}),
  });
};

export const trackHybridGatewayUpdate = (
  orgUuid: string | undefined,
  gatewayId: string,
  functionalityType: string,
  environmentId?: string
) => {
  trackEvent('hybrid-gateway-update', {
    org: orgUuid,
    gatewayId,
    functionalityType,
    environmentId,
  });
};

export const trackHybridGatewayDelete = (
  orgUuid: string | undefined,
  gatewayId: string
) => {
  trackEvent('hybrid-gateway-delete', {
    org: orgUuid,
    gatewayId,
  });
};

// API deployments to Platform Gateway (Hybrid Gateway deployments)
export const trackHybridGatewayDeploymentCreate = (params: {
  orgUuid?: string;
  gatewayId: string;
  gatewayName?: string;
  apiId?: string;
  deploymentId?: string;
  base?: string;
  hasBuildId?: boolean;
  hasEndpointOverride?: boolean;
}) => {
  const {
    orgUuid,
    gatewayId,
    gatewayName,
    apiId,
    deploymentId,
    base,
    hasBuildId,
    hasEndpointOverride,
  } = params;

  trackEvent('hybrid-gateway-deployment-create', {
    org: orgUuid,
    gatewayId,
    gatewayName,
    apiId,
    deploymentId,
    base,
    hasBuildId: hasBuildId ? 'true' : 'false',
    hasEndpointOverride: hasEndpointOverride ? 'true' : 'false',
  });
};

export const trackHybridGatewayDeploymentRedeploy = (params: {
  orgUuid?: string;
  gatewayId: string;
  gatewayName?: string;
  apiId?: string;
  deploymentId?: string;
  hasBuildId?: boolean;
}) => {
  const { orgUuid, gatewayId, gatewayName, apiId, deploymentId, hasBuildId } =
    params;

  trackEvent('hybrid-gateway-deployment-redeploy', {
    org: orgUuid,
    gatewayId,
    gatewayName,
    apiId,
    deploymentId,
    hasBuildId: hasBuildId ? 'true' : 'false',
  });
};

export const trackHybridGatewayDeploymentUndeploy = (params: {
  orgUuid?: string;
  gatewayId: string;
  gatewayName?: string;
  apiId?: string;
  deploymentId?: string;
}) => {
  const { orgUuid, gatewayId, gatewayName, apiId, deploymentId } = params;

  trackEvent('hybrid-gateway-deployment-undeploy', {
    org: orgUuid,
    gatewayId,
    gatewayName,
    apiId,
    deploymentId,
  });
};

export const trackHybridGatewayDeploymentDelete = (params: {
  orgUuid?: string;
  gatewayId: string;
  gatewayName?: string;
  apiId?: string;
  deploymentId?: string;
}) => {
  const { orgUuid, gatewayId, gatewayName, apiId, deploymentId } = params;

  trackEvent('hybrid-gateway-deployment-delete', {
    org: orgUuid,
    gatewayId,
    gatewayName,
    apiId,
    deploymentId,
  });
};
